import cn from 'classnames';
import PropTypes from 'prop-types';
import {
  useId,
  useRef,
  useEffect
} from 'react';

import LdsIcon from '../LdsIcon/index.jsx';

import handleTabAway from '../../util/handleTabAway.js';
import useClickOutside from '../../hooks/useClickOutside.js';
import getFocusableElements from '../../util/getFocusableElements.js';

import Link from './Link.jsx';
import Button from './Button.jsx';

// JSDoc type definitions
/**
 * @typedef LdsDropdownProps
 * @property {React.JSX.Element} [label] - Label to display on the dropdown button
 * @property {Object} buttonProps - Optional object of html attributes to apply to the button
 * @property {Object} dropdownProps - Optional object of html attributes to apply to the dropdown
 * @property {Function} [onExpand] - Event that will be fired after the dropdown opens and its animation is complete
 * @property {Function} [onCollapse] - Event that will be fired after the dropdown closes and its animation is complete
 * @property {boolean} [autoFocusFirstItem] - Boolean value to to set if first focusable item in the dropdown should automatically be focused when the dropdown opens. Defaults to true.
 * @property {boolean} [showExpandIcon] - Boolean value to to show the expand chevron icon in the dropdown button. Defaults to true.
 * @property {boolean} [isOpen] - Boolean state value to set if the dropdown is open or closed.
 * @property {Function} [setIsOpen] - State function to set the isOpen value.
 * @property {React.ReactNode} [children] - The children of the component to be displayed in the dropdown
 */
/**
 * LdsDropdown component
 * @param {LdsDropdownProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element}
 */
export default function LdsDropdown({
  label,
  buttonProps = {},
  dropdownProps = {},
  autoFocusFirstItem = true,
  showExpandIcon = true,
  onExpand,
  onCollapse,
  children,
  isOpen = false,
  setIsOpen = () => {},
}) {
  // Filter className out of generic props oject to avoid potential overwrites
  const filteredButtonProps = { ...buttonProps };
  delete filteredButtonProps.className;
  delete filteredButtonProps.onClick;

  const filteredDropdownProps = { ...dropdownProps };
  delete filteredDropdownProps.className;

  const dropdownId = useId();

  const dropdownRef = useRef(null);
  const wrapperRef = useClickOutside(() => {
    if (isOpen) setIsOpen(false);
  });

  const handleKeyDown = (e) => {
    const callback = () => {
      setIsOpen(false);
    };
    return handleTabAway(e, dropdownRef, callback);
  };

  /*
    onExpand and onCollapse are optional function props that will be called after the
    dropdown opens or closes. Because the dropdown has an animation delay we wait for the
    transition to end before calling the functions. This can be used for something like focusing
    an element when the dd opens or closes.
  */
  useEffect(() => {
    const dropdownRefCurrent = dropdownRef.current;

    const transitionCallback = () => {
      // Focus first focusable item in the dropdown if autoFocusFirstItem is enabled
      // This has to be done after the transition ends in order to work properly
      if (isOpen && autoFocusFirstItem) {
        const focusable = getFocusableElements(dropdownRef.current);
        if (!focusable || focusable.length === 0) return;

        focusable[0].focus();
      }

      if (isOpen && onExpand) onExpand();
      if (!isOpen && onCollapse) onCollapse();
    };

    dropdownRefCurrent.addEventListener('transitionend', transitionCallback);
    return () => {
      dropdownRefCurrent.removeEventListener('transitionend', transitionCallback);
    };
  }, [isOpen, onExpand, onCollapse, autoFocusFirstItem]);

  const handleClick = (e) => {
    setIsOpen(!isOpen);
    if (buttonProps.onClick) buttonProps.onClick(e, isOpen);
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'lds-dropdown-wrapper',
        {
          open: isOpen,
        }
      )}
    >
      <button
        type="button"
        className={cn(
          'lds-button-base',
          'lds-dropdown-button',
          buttonProps?.className,
          {
            open: isOpen,
          }
        )}
        aria-expanded={isOpen}
        aria-controls={dropdownId}
        onClick={handleClick}
        {...filteredButtonProps}
      >
        {label}
        { showExpandIcon
          && <LdsIcon className="lds-dropdown-expand-icon" name="caret-down" inline decorative />
        }
      </button>
      <div
        id={dropdownId}
        ref={dropdownRef}
        className={cn(
          'lds-dropdown',
          dropdownProps?.className,
          {
            open: isOpen,
          }
        )}
        inert={isOpen ? null : ''}
        aria-hidden={!isOpen}
        onKeyDown={handleKeyDown}
        {...filteredDropdownProps}
      >
        <div
          className="dropdown-inner-expandable"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

LdsDropdown.Link = Link;
LdsDropdown.Button = Button;

LdsDropdown.propTypes = {
  label: PropTypes.any.isRequired,
  buttonProps: PropTypes.object,
  onExpand: PropTypes.func,
  onCollapse: PropTypes.func,
};
